import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PoClosureRequestStatus } from "models/po-closure";
import { usePoClosureRequest } from "src/hooks/po-closure/usePoClosureRequest";
import { getNetSuiteLink } from "src/util/helpers";
import StatusChip from "../general/StatusChip";

const PoClosure: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const { request, loading, error, fetchPoClosureRequest } = usePoClosureRequest();

  const load = useCallback(async () => {
    if (!id) return;
    setRefreshing(true);
    try {
      await fetchPoClosureRequest(id);
    } finally {
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (request?.status === PoClosureRequestStatus.PENDING) {
      const timer = setInterval(() => {
        load();
      }, 5000);
      return () => clearInterval(timer);
    }
    return undefined;
  }, [request?.status, load]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !request) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <Typography color="error">{error || "PO closure request not found."}</Typography>
      </Box>
    );
  }

  const requesterDisplayName = request.requester.name?.trim() || request.requester.email;

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate("/po-closures")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">PO {request.input.poNumber}</Typography>
          <StatusChip status={request.status} />
          {refreshing ? <CircularProgress size={20} /> : null}
        </Box>

        <Box mb={2}>
          <Typography variant="h6">Request Context</Typography>
          <Typography variant="body2">
            <strong>Requester:</strong> {requesterDisplayName}
          </Typography>
          <Typography variant="body2">
            <strong>Request ID:</strong> {request.requestId}
          </Typography>
          <Typography variant="body2">
            <strong>Created:</strong>{" "}
            {new Date(request.createdAt).toLocaleString("en-US")}
          </Typography>
          <Typography variant="body2">
            <strong>Updated:</strong>{" "}
            {request.updatedAt
              ? new Date(request.updatedAt).toLocaleString("en-US")
              : "N/A"}
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="h6">PO Details</Typography>
          <Typography variant="body2">
            <strong>PO Number:</strong>{" "}
            <Link
              href={getNetSuiteLink("PurchaseOrder", request.input.poInternalId)}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {request.input.poNumber}
            </Link>
          </Typography>
          <Typography variant="body2">
            <strong>Vendor:</strong> {request.input.vendorName}
          </Typography>
          <Typography variant="body2">
            <strong>PO Internal ID:</strong> {request.input.poInternalId}
          </Typography>
          <Typography variant="body2">
            <strong>Total:</strong> {request.input.amountTotal ?? "N/A"} {request.input.currency || ""}
          </Typography>
          <Typography variant="body2">
            <strong>Remaining:</strong> {request.input.amountRemaining ?? "N/A"} {request.input.currency || ""}
          </Typography>
          <Typography variant="body2">
            <strong>Status At Submission:</strong> {request.input.statusAtSubmission || "N/A"}
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="h6">Reason</Typography>
          <Typography variant="body2">{request.input.reason}</Typography>
        </Box>

        <Box>
          <Typography variant="h6">Processing Output</Typography>
          <Typography variant="body2">
            <strong>NetSuite Status:</strong> {request.output.netSuiteStatus || "N/A"}
          </Typography>
          <Typography variant="body2">
            <strong>NetSuite Message:</strong> {request.output.netSuiteMessage || "N/A"}
          </Typography>
          <Typography variant="body2">
            <strong>Closed At:</strong>{" "}
            {request.output.closedAt
              ? new Date(request.output.closedAt).toLocaleString("en-US")
              : "N/A"}
          </Typography>
          {request.output.errorCode ? (
            <Typography variant="body2" color="error">
              <strong>Error:</strong> {request.output.errorCode} -{" "}
              {request.output.errorMessage || "N/A"}
            </Typography>
          ) : null}
        </Box>
      </Paper>
    </Box>
  );
};

export default PoClosure;
